import React, { Fragment, useEffect, useRef, useState } from "react";
import { City, QuarantineCities, QuarantineCompany, QuarantineUniversity, State } from "../../Models";
import useApiToken from "../../hooks/useApiToken";
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDoubleUpIcon,
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
  MinusIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { Link, useLocation } from "react-router-dom";
import DataGrid from "./Table/DataGrid";
import { Disclosure } from "@headlessui/react";
import Actions from "./ActionLog/Actions";
import { useNotification } from "../../context/NotificationContext";
import { History } from "@mui/icons-material";
import TableHeader from "./Table/TableHeader";
import { PopUp } from "../../Models";
import TagManagementModal from "../Shared/Modals/TagManagementModal";
import AssignTagModal from "../Shared/Modals/AssignTagModal";
import { NIL } from "uuid";
import { Tags } from "../Ranking/Sidebar/LeftSidebar/RankingSideBarBody";
import TagFilter from "../Shared/Filter/TagFilter";
import SimilarCityGroupsModal, { SuggestedCityGroup } from "./SubComponents/SimilarCityGroupsModal";
import SimilarUniversityGroupsModal, { SimilarUniversityGroup } from "./SubComponents/SimilarUniversityGroupsModal";
import SimilarCompanyGroupsModal, { SimilarCompanyGroup } from "./SubComponents/SimilarCompanyGroupsModal";
import { GetCities, GetStates } from "../../api/masterdata.api";
import { Filters } from "../../api/config";
import { fetchQuarantineCompanies, fetchQuarantineUniversities, getSimilarCityGroupsWithSuggestions, getSimilarUniversityGroups, refreshSimilarUniversityGroupsCache, getSimilarCompanyGroups, refreshSimilarCompanyGroupsCache, getUnconfirmedCities } from "../../api/quarantine.api";
import { InformationCircleIcon } from "@heroicons/react/20/solid";
import ReactTooltip from "../Shared/ReactToolTip";
import { getTags } from "../../api/tag.api";

// ============================================================================
// TYPES & CONFIGURATION
// ============================================================================

const TABS = ["Companies", "Universities", "Cities"] as const;
type TabType = typeof TABS[number];

// Union type for all possible quarantine data
export type QuarantineDataType = QuarantineCompany | QuarantineUniversity | QuarantineCities;

// Map tab to data type
type TabToDataType<T extends TabType> = 
  T extends "Companies" ? QuarantineCompany :
  T extends "Universities" ? QuarantineUniversity :
  QuarantineCities;

export interface Sort {
  name: string;
  dsc: boolean;
}

interface FilterOption {
  value: string | number | boolean | null;
  label: string;
  checked: boolean;
  extraData?: number;
}

interface LocationFilter {
  id: string;
  name: string;
  options: FilterOption[];
  filteredOptions: FilterOption[];
}

// ============================================================================
// TAB CONFIGURATION - Define behavior per tab
// ============================================================================

interface TabConfig<T extends TabType = TabType> {
  name: T;
  route: string;
  fetchData: (
    token: string,
    page: number,
    pageSize: number,
    query: string,
    sortType?: string,
    sortDesc?: boolean,
    filters?: Filters[],
    univType?: number,
    cityType?: number,
    selectedStates?: string[],
    showConfirmed?: boolean
  ) => Promise<{ records: TabToDataType<T>[]; totalRecords: number }>;
  showCountFilter: boolean;
  showTypeFilter: boolean;
  showCityTypeFilter: boolean;
  showLocationFilter: boolean;
  showStateFilter: boolean;
  showTagManagement: boolean;
  showSimilarCityGroups: boolean;
  isCompanyType: boolean;
}

const TAB_CONFIGS: { [K in TabType]: TabConfig<K> } = {
  Companies: {
    name: "Companies",
    route: "/quarantine/companies",
    fetchData: async (token, page, pageSize, query, sortType, sortDesc, filters, univType, cityType) => {
      return await fetchQuarantineCompanies(
        token,
        page,
        pageSize,
        query,
        sortType,
        sortDesc,
        filters,
        univType,
        cityType
      );
    },
    showCountFilter: true,
    showTypeFilter: true,
    showCityTypeFilter: true,
    showLocationFilter: true,
    showStateFilter: false,
    showTagManagement: true,
    showSimilarCityGroups: false,
    isCompanyType: true,
  },
  Universities: {
    name: "Universities",
    route: "/quarantine/universities",
    fetchData: async (token, page, pageSize, query, sortType, sortDesc, filters, univType, cityType) => {
      return await fetchQuarantineUniversities(
        token,
        page,
        pageSize,
        query,
        sortType,
        sortDesc,
        filters,
        univType,
        cityType
      );
    },
    showCountFilter: true,
    showTypeFilter: true,
    showCityTypeFilter: true,
    showLocationFilter: true,
    showStateFilter: false,
    showTagManagement: true,
    showSimilarCityGroups: false,
    isCompanyType: false,
  },
  Cities: {
    name: "Cities",
    route: "/quarantine/cities",
    fetchData: async (token, page, pageSize, query, sortType, sortDesc, filters, univType, cityType, selectedStates, showConfirmed) => {
      const { records, totalRecords } = await getUnconfirmedCities(
        selectedStates || [],
        query,
        sortType,
        sortDesc,
        page,
        pageSize,
        showConfirmed || false,
        token
      );
      
      // Parse actiongpt field for cities
      const parsedRecords = records.map((record: any) => {
        let parsedActionGpt: any = record.actiongpt;

        if (typeof record.actiongpt === "string" && !record.actiongpt.trim().startsWith('{\"')) {
          try {
            const inner = record.actiongpt.replace(/^{|}$/g, "").trim();
            const pairs = inner.split(",").map((s: string) => s.trim());

            const parsed: any = {};
            for (const pair of pairs) {
              const [key, ...rest] = pair.split("=");
              const value = rest.join("=").trim();
              parsed[key.trim()] = value === "" || value === "null" ? null : value;
            }

            parsedActionGpt = parsed;
          } catch (e) {
            console.warn("Failed to parse actiongpt for record:", record, e);
          }
        }

        return {
          ...record,
          actiongpt: parsedActionGpt,
        };
      });

      return { records: parsedRecords, totalRecords };
    },
    showCountFilter: false,
    showTypeFilter: false,
    showCityTypeFilter: false,
    showLocationFilter: false,
    showStateFilter: true,
    showTagManagement: false,
    showSimilarCityGroups: true,
    isCompanyType: false,
  },
};

// ============================================================================
// REUSABLE COMPONENT TEMPLATES
// ============================================================================

// Tab Navigation Template
const TabNavigation: React.FC<{
  tabs: readonly TabType[];
  selected: TabType;
}> = ({ tabs, selected }) => {
  const getTabIndex = () => tabs.indexOf(selected);

  return (
    <div className="flex text-sm justify-between items-center py-2 mx-2 my-1 mt-2 rounded-full bg-white border border-gray-200 relative dark:bg-darkbg dark:border-gray-500">
      <div
        className="absolute bg-entntblue rounded-full inset-y-0 left-0 h-[35px] w-1/3 z-0 transform transition-all duration-300 ease-in-out"
        style={{ transform: `translateX(${getTabIndex() * 100}%)` }}
      />
      
      {tabs.map((tab) => (
        <Link 
          key={tab} 
          to={TAB_CONFIGS[tab].route} 
          className="z-10 flex-1 text-center"
        >
          <button
            type="button"
            className={`inline-flex justify-center items-center w-full text-sm font-medium transition ${
              selected === tab ? "text-gray-100" : "text-gray-400"
            }`}
          >
            {tab}
          </button>
        </Link>
      ))}
    </div>
  );
};

// Search Input Template
const SearchInput: React.FC<{
  searchValue: string;
  selectedOption: City | null;
  filteredOptions: City[];
  disabled?: boolean;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onOptionSelect: (option: City) => void;
  onClear: () => void;
}> = ({ 
  searchValue, 
  selectedOption, 
  filteredOptions, 
  disabled,
  onSearchChange, 
  onOptionSelect,
  onClear 
}) => (
  <div className="relative w-full mx-2">
    <div className="relative flex items-center">
      <input
        type="text"
        name="search"
        id="search"
        className="block w-full border-0 pl-3 py-2 text-gray-900 ring-1 dark:text-white ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-indigo-500 sm:text-sm bg-transparent rounded-md"
        placeholder="Search"
        value={searchValue}
        disabled={disabled}
        onChange={onSearchChange}
      />
      {selectedOption && (
        <div
          className="absolute right-2 hover:text-gray-500 cursor-pointer"
          onClick={onClear}
        >
          <XMarkIcon className="h-5 w-5 dark:text-white text-gray-300" />
        </div>
      )}
    </div>
    
    {filteredOptions.length > 0 && searchValue && !selectedOption && (
      <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-md ring-1 ring-black ring-opacity-5 dark:bg-darkbglight">
        <ul className="max-h-40 overflow-auto py-1">
          {filteredOptions.map((option, index) => (
            <li
              key={index}
              className="cursor-pointer select-none relative py-2 pl-3 pr-9 text-gray-900 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => onOptionSelect(option)}
            >
              {option.name} {option.alternateNames ? `(${option.alternateNames})` : null}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

// Count Filter Template
const CountFilter: React.FC<{
  minCount: string;
  maxCount: string;
  disabled?: boolean;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
  onApply: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}> = ({ minCount, maxCount, disabled, onMinChange, onMaxChange, onApply, onKeyDown }) => (
  <Disclosure as="div" className="border-b border-gray-200 py-4 dark:border-gray-500">
    {({ open }) => (
      <>
        <Disclosure.Button className="flex w-full items-center justify-between px-3 text-sm text-gray-400 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-200">
          <span className="font-medium text-gray-900 dark:text-gray-300">Count</span>
          {open ? (
            <MinusIcon className="h-5 w-5" aria-hidden="true" />
          ) : (
            <PlusIcon className="h-5 w-5" aria-hidden="true" />
          )}
        </Disclosure.Button>
        <Disclosure.Panel className="flex flex-col gap-[1rem] px-6 pt-4 pb-2 text-sm text-gray-500">
          <div className="flex justify-between items-center">
            <input
              type="text"
              value={minCount}
              onChange={(e) => onMinChange(e.target.value)}
              placeholder="min"
              className="w-[6rem] text-sm py-[3px] rounded-md"
              onKeyDown={onKeyDown}
              disabled={disabled}
            />
            <input
              type="text"
              value={maxCount}
              onChange={(e) => onMaxChange(e.target.value)}
              placeholder="max"
              className="w-[6rem] text-sm py-[3px] rounded-md"
              onKeyDown={onKeyDown}
              disabled={disabled}
            />
          </div>
          <button
            className="disabled:cursor-not-allowed text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-md text-sm px-4 py-1.5 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800"
            onClick={onApply}
            disabled={disabled}
          >
            Apply
          </button>
        </Disclosure.Panel>
      </>
    )}
  </Disclosure>
);

// Type Filter Template
const TypeFilter: React.FC<{
  title: string;
  selectedType: number;
  options: { value: number; label: string }[];
  onChange: (type: number) => void;
}> = ({ title, selectedType, options, onChange }) => (
  <Disclosure as="div" className="border-b border-gray-200 py-4 dark:border-gray-500">
    {({ open }) => (
      <>
        <Disclosure.Button className="flex w-full items-center justify-between px-3 text-sm text-gray-400 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-200">
          <span className="font-medium text-gray-900 dark:text-gray-300">{title}</span>
          {open ? (
            <MinusIcon className="h-5 w-5" aria-hidden="true" />
          ) : (
            <PlusIcon className="h-5 w-5" aria-hidden="true" />
          )}
        </Disclosure.Button>
        <Disclosure.Panel className="flex flex-col gap-2 px-3 pt-4 pb-2 text-sm text-gray-500">
          {options.map((option) => (
            <label key={option.value} className="flex items-center gap-2">
              <input
                className="rounded z-10 border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:border-gray-100"
                type="checkbox"
                checked={selectedType === option.value}
                onChange={() => onChange(option.value)}
              />
              {option.label}
            </label>
          ))}
        </Disclosure.Panel>
      </>
    )}
  </Disclosure>
);

// Location Filter Template
const LocationFilterComponent: React.FC<{
  locationFilter: LocationFilter;
  locationSearch: string;
  disabled?: boolean;
  onSearchChange: (value: string) => void;
  onOptionChange: (option: FilterOption) => void;
}> = ({ locationFilter, locationSearch, disabled, onSearchChange, onOptionChange }) => (
  <Disclosure as="div" className="border-b border-gray-200 py-4 px-3 dark:border-gray-500">
    {({ open, close }) => (
      <div>
        <div className="relative">
          <h3 className="-my-4 flow-root">
            <Disclosure.Button className="flex w-full items-center justify-between py-3 text-sm text-gray-400 hover:text-gray-500">
              <span className="font-medium text-gray-900 dark:text-gray-300">
                {locationFilter.name}
              </span>
              <span className="ml-3 flex items-center">
                <div className="relative w-full cursor-default overflow-hidden rounded-md bg-white text-left ring-1 ring-gray-300 sm:text-sm dark:ring-gray-500">
                  <input
                    type="text"
                    value={locationSearch}
                    placeholder="Search City"
                    disabled={disabled}
                    className="disabled:cursor-not-allowed w-full border-none py-1 pl-3 pr-10 text-sm text-gray-900 focus:ring-0 placeholder:text-gray-400 dark:text-gray-300 dark:bg-darkbg"
                    onChange={(event) => onSearchChange(event.target.value)}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon
                      className="h-5 w-5 text-gray-400 dark:text-gray-400"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </span>
            </Disclosure.Button>
          </h3>

          <Disclosure.Panel
            className="dark:bg-darkbglight dark:border-gray-500 shadow-sm pl-1 py-1 h-fit absolute bg-white rounded-md z-50 border border-gray-200 bottom-[2.6rem] -right-[0.20rem]"
          >
            <div className="max-h-[10.5rem] min-w-[11.8rem] pl-2 py-1 overflow-auto thin-scroll bg-transparent z-10">
              <div className="space-y-2">
                {locationFilter.filteredOptions.map((option, optionIdx) => (
                  <div key={optionIdx} className="flex items-center">
                    <input
                      id={`location-filter-${optionIdx}`}
                      type="checkbox"
                      onChange={() => onOptionChange(option)}
                      checked={option.checked}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label
                      htmlFor={`location-filter-${optionIdx}`}
                      className="ml-3 text-sm text-gray-600 flex justify-between w-full pr-2 dark:text-gray-400"
                    >
                      <span>{option.label}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </Disclosure.Panel>
        </div>
        
        {open && (
          <div
            className="fixed inset-0 z-20"
            onClick={() => close()}
          />
        )}

        <div className="z-10 text-xs flex flex-wrap box-border mt-2">
          {locationFilter.options.map((o, index) =>
            o.checked ? (
              <span
                key={o.label + index}
                className="flex items-center bg-blue-100 text-entntblue py-1 px-2 mr-2 mt-2 rounded-full relative"
              >
                {o.label}
                <XMarkIcon
                  onClick={() => onOptionChange(o)}
                  className="h-4 w-4 cursor-pointer text-gray-500 ml-1 hover:text-gray-600"
                />
              </span>
            ) : (
              <Fragment key={index} />
            )
          )}
        </div>
      </div>
    )}
  </Disclosure>
);

// State Filter Template
const StateFilterComponent: React.FC<{
  states: State[];
  stateSearch: string;
  selectedStates: string[];
  disabled?: boolean;
  showConfirmed: boolean;
  onSearchChange: (value: string) => void;
  onStateChange: (stateId: string) => void;
  onConfirmedChange: (checked: boolean) => void;
}> = ({ 
  states, 
  stateSearch, 
  selectedStates, 
  disabled, 
  showConfirmed,
  onSearchChange, 
  onStateChange,
  onConfirmedChange 
}) => (
  <Disclosure as="div" className="border-b border-gray-200 py-4 px-3 dark:border-gray-500">
    {({ open, close }) => (
      <div>
        <div className="relative">
          <h3 className="-my-4 flow-root">
            <Disclosure.Button className="flex w-full items-center justify-between py-3 text-sm text-gray-400 hover:text-gray-500">
              <span className="font-medium text-gray-900 dark:text-gray-300">States</span>
              <span className="ml-3 flex items-center">
                <div className="relative w-full cursor-default overflow-hidden rounded-md bg-white text-left ring-1 ring-gray-300 sm:text-sm dark:ring-gray-500">
                  <input
                    type="text"
                    value={stateSearch}
                    placeholder="Search State"
                    disabled={disabled}
                    className="disabled:cursor-not-allowed w-full border-none py-1 pl-3 pr-10 text-sm text-gray-900 focus:ring-0 placeholder:text-gray-400 dark:text-gray-300 dark:bg-darkbg"
                    onChange={(event) => onSearchChange(event.target.value)}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-400" aria-hidden="true" />
                  </div>
                </div>
              </span>
            </Disclosure.Button>
          </h3>
          
          <div className="flex flex-row items-center mt-10 gap-4">
            <input
              onChange={(e) => onConfirmedChange(e.target.checked)}
              checked={showConfirmed}
              type="checkbox"
              id="confirmedCities"
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:border-gray-100"
            />
            <label htmlFor="confirmedCities" className="text-gray-400 dark:text-gray-400">
              Only show confirmed cities
            </label>
          </div>

          <Disclosure.Panel className="dark:bg-darkbglight dark:border-gray-500 shadow-sm pl-1 py-1 h-fit absolute bg-white rounded-md z-50 border border-gray-200 top-full left-0 mt-1">
            <div className="max-h-[10.5rem] min-w-[11.8rem] pl-2 py-1 overflow-auto thin-scroll bg-transparent z-10">
              <div className="space-y-2">
                {states.map((state) => (
                  <div key={state.id} className="flex items-center">
                    <input
                      id={`state-filter-${state.id}`}
                      type="checkbox"
                      onChange={() => onStateChange(state.id)}
                      checked={selectedStates.includes(state.id)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label
                      htmlFor={`state-filter-${state.id}`}
                      className="ml-3 text-sm text-gray-600 flex justify-between w-full pr-2 dark:text-gray-400"
                    >
                      <span>{state.name}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </Disclosure.Panel>
        </div>
        
        {open && <div className="fixed inset-0 z-20" onClick={() => close()} />}
        
        <div className="z-10 text-xs flex flex-wrap box-border mt-2">
          {states
            .filter((s) => selectedStates.includes(s.id))
            .map((s) => (
              <span
                key={s.id}
                className="flex items-center bg-blue-100 text-entntblue py-1 px-2 mr-2 mt-2 rounded-full relative"
              >
                {s.name}
                <XMarkIcon
                  onClick={() => onStateChange(s.id)}
                  className="h-4 w-4 cursor-pointer text-gray-500 ml-1 hover:text-gray-600"
                />
              </span>
            ))}
        </div>
      </div>
    )}
  </Disclosure>
);

// Sidebar Toggle Button Template
const SidebarToggle: React.FC<{
  isOpen: boolean;
  onClick: () => void;
}> = ({ isOpen, onClick }) => (
  <div
    className={`absolute top-40 ${
      isOpen ? "-right-2 shadow-md" : "-right-4 shadow-xl"
    } h-16 border border-gray-300 rounded-md flex items-center justify-center cursor-pointer bg-white dark:bg-darkbglight dark:border-gray-500 dark:text-gray-300`}
    onClick={onClick}
  >
    {isOpen ? (
      <ChevronDoubleLeftIcon width={16} />
    ) : (
      <ChevronDoubleRightIcon width={16} />
    )}
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function Quarantine() {
  const token = useApiToken();
  const { error: notifyError, success, info } = useNotification();
  const location = useLocation();
  
  // Core State
  const [selected, setSelected] = useState<TabType>(TABS[0]);
  const [currentConfig, setCurrentConfig] = useState<TabConfig>(TAB_CONFIGS[TABS[0]]);
  const [selectedRows, setSelectedRows] = useState<QuarantineDataType[]>([]);
  const [query, setQuery] = useState<string>("");
  const [scrollToTop, setScrollToTop] = useState<boolean>(false);
  const [quarantineData, setQuarantineData] = useState<QuarantineDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);
  const [totalRecords, setTotalRecords] = useState<number>(13);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>();
  
  // Filter State
  const [minCount, setMinCount] = useState<string>("0");
  const [maxCount, setMaxCount] = useState<string>("5000");
  const [sortOption, setSortOption] = useState<Sort>();
  const [selectedCompanyLocations, setSelectedCompanyLocations] = useState<string[]>([]);
  const [locationSearch, setLocationSearch] = useState("");
  const [cities, setCities] = useState<City[]>([]);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const [states, setStates] = useState<State[]>([]);
  const [stateSearch, setStateSearch] = useState("");
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [filterState, setFilterState] = useState<State[]>([]);
  const [univType, setUnivType] = useState<number>(0);
  const [cityType, setCityType] = useState<number>(0);
  
  // UI State
  const [showActions, setShowActions] = useState<boolean>(false);
  const [isManagementModal, setIsManagementModal] = useState<boolean>(false);
  const [pendingNotification, setPendingNotification] = useState<PopUp | null>(null);
  const [assignTagModal, setAssignTagModal] = useState<boolean>(false);
  const [tags, setTags] = useState<Tags[]>([]);
  const [showConfirmed, setShowConfirmed] = useState<boolean>(false);
  const [showRejectModalParent, setShowRejectModalParent] = useState<boolean>(false);
  const [subCityClickPopUpParent, setSubCityClickPopUpParent] = useState<boolean>(false);
  const [majorCityClickPopUpParent, setMajorCityClickPopUpParent] = useState<boolean>(false);
  const [alternateNameClickPopUpParent, setAlternateNameClickPopUpParent] = useState<boolean>(false);
  const [gptCitySelected, setGptCitySelected] = useState();
  const [gptCityDetails, setGptCityDetails] = useState<Record<string, any>>({});
  const [isSimilarCityModalOpen, setIsSimilarCityModalOpen] = useState(false);
  const [similarCityGroups, setSimilarCityGroups] = useState<SuggestedCityGroup[]>([]);
  const [totalSimilarGroups, setTotalSimilarGroups] = useState(0);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [selectedOption, setSelectedOption] = useState<City | null>(null);

  const [isSimilarUniModalOpen, setIsSimilarUniModalOpen] = useState(false);
  const [similarUniGroups, setSimilarUniGroups] = useState<SimilarUniversityGroup[]>([]);
  const [totalSimilarUniGroups, setTotalSimilarUniGroups] = useState(0);
  const [isLoadingUniModal, setIsLoadingUniModal] = useState(false);
  const [uniModalError, setUniModalError] = useState<string | null>(null);
  const [uniModalPage, setUniModalPage] = useState(1);

  const [isSimilarCompanyModalOpen, setIsSimilarCompanyModalOpen] = useState(false);
  const [similarCompanyGroups, setSimilarCompanyGroups] = useState<SimilarCompanyGroup[]>([]);
  const [totalSimilarCompanyGroups, setTotalSimilarCompanyGroups] = useState(0);
  const [isLoadingCompanyModal, setIsLoadingCompanyModal] = useState(false);
  const [companyModalError, setCompanyModalError] = useState<string | null>(null);
  const [companyModalPage, setCompanyModalPage] = useState(1);
  const [urlValidationResults, setUrlValidationResults] = useState<Record<string, { isReachable: boolean; statusCode: number; message: string } | 'loading'>>({});

  const initialLocationFilter: LocationFilter = {
    id: "workLocation1City.Id",
    name: "Location",
    options: [],
    filteredOptions: [],
  };

  const [locationFilter, setLocationFilter] = useState(initialLocationFilter);

  const TYPE_FILTER_OPTIONS = [
    { value: 0, label: 'All' },
    { value: 1, label: 'New' },
    { value: 2, label: 'Old' },
  ];

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Update current config when route changes
  useEffect(() => {
    let newTab: TabType = TABS[0];
    
    if (location.pathname.includes("/companies")) newTab = "Companies";
    else if (location.pathname.includes("/universities")) newTab = "Universities";
    else if (location.pathname.includes("/cities")) newTab = "Cities";
    
    setSelected(newTab);
    setCurrentConfig(TAB_CONFIGS[newTab]);
    
    // Reset filters
    setLocationFilter(initialLocationFilter);
    setQuarantineData([]);
    setSelectedRows([]);
    setSelectedCompanyLocations([]);
    setPage(1);
    setQuery("");
    setMinCount("0");
    setMaxCount("5000");
    setLocationSearch("");
    setShowActions(false);
    setShowConfirmed(false);
    setUnivType(0);
    setCityType(0);
  }, [location.pathname]);

  // Load cities and states based on tab config
  useEffect(() => {
    GetCities(token || "").then((c) => {
      setCities(c);
    });
    
    if (currentConfig.showStateFilter) {
      GetStates(token || "").then((s) => {
        setStates(s);
        setFilterState(s);
      });
    }
  }, [selected, currentConfig]);

  // Filter cities based on search
  useEffect(() => {
    let filteredCity = cities;
    if (locationSearch !== "")
      filteredCity = cities.filter((c) =>
        c.name.toLowerCase().startsWith(locationSearch.toLowerCase())
      );
    const filteredCities = locationFilter.options.filter((lf) => {
      return filteredCity.some((fc) => fc.id === lf.value);
    });
    setLocationFilter((prev) => ({ ...prev, filteredOptions: filteredCities }));
  }, [locationSearch, cities, locationFilter.options]);

  // Update location filter options
  useEffect(() => {
    const updatedLocationFilterOption = cities.map((c) => ({
      checked: false,
      value: c.id,
      label: c.name,
    }));

    const locationCityFilter = {
      ...initialLocationFilter,
      options: updatedLocationFilterOption,
    };
    setLocationFilter(locationCityFilter);
  }, [cities]);

  // Filter states based on search
  useEffect(() => {
    if (stateSearch) {
      const tempStates: State[] = states.filter((s) => 
        s.name.toLowerCase().includes(stateSearch.toLowerCase())
      );
      setFilterState(tempStates);
    } else {
      setFilterState(states);
    }
  }, [stateSearch, states]);

  // Infinite scroll handler
  useEffect(() => {
    let timeoutId: any;

    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const isScrollPresent = documentHeight > windowHeight;
        const isAtTop = window.scrollY === 0;

        setScrollToTop(isScrollPresent && !isAtTop);

        if (scrollTop + windowHeight + 1 >= documentHeight) {
          setPage((prev) => {
            if (!totalPages || prev < totalPages) {
              return prev + 1;
            }
            return prev;
          });
        }
      }, 1000);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [totalPages]);

  // Reset page on sort/filter change
  useEffect(() => {
    setPage(1);
  }, [sortOption, selectedStates, univType, cityType, locationFilter]);

  // Reset and refetch when closing actions log
  useEffect(() => {
    if (!showActions) {
      setPage(1);
      setQuarantineData([]);
    }
  }, [showActions]);

  // Fetch data on page/filter change
  useEffect(() => {
    const fetchData = async () => {
      if (sortOption) {
        await fetchQuarantineData(sortOption.name, sortOption.dsc, buildFilters());
      } else {
        await fetchQuarantineData(undefined, undefined, buildFilters());
      }
    };
    
    setTimeout(() => {
      if (!showActions) {
        fetchData();
      }
    }, 500);
  }, [page, sortOption, selectedStates, univType, cityType, showActions, showConfirmed, token]);

  // Reset on query change
  useEffect(() => {
    setPage(1);
    setQuarantineData([]);
    setSelectedRows([]);
    setSortOption({ name: "Name", dsc: false });
  }, [query]);

  // Reset on tab change
  useEffect(() => {
    setQuarantineData([]);
    setSelectedCompanyLocations([]);
    setSelectedRows([]);
    setPage(1);
    setQuery("");
    setMinCount("0");
    setMaxCount("5000");
    setLocationSearch("");
    setShowActions(false);
    
    const fetchData = async () => {
      await fetchQuarantineData();
    };
    fetchData();
  }, [selected]);

  // Clear selected option if search changes
  useEffect(() => {
    if (selectedOption && query !== selectedOption.name) {
      setSelectedOption(null);
    }
  }, [query, selectedOption]);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  const buildFilters = (): Filters[] => {
    const filters: Filters[] = [];
    
    if (currentConfig.showCountFilter) {
      filters.push({
        FilterType: "count",
        MinCount: parseInt(minCount),
        MaxCount: parseInt(maxCount),
      });
    }
    
    if (currentConfig.showLocationFilter) {
      filters.push({
        FilterType: "location",
        Locations: locationFilter.options
          .filter((o) => o.checked)
          .map((o) => o.label),
      });
    }
    
    return filters;
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleSetShow = (show: boolean) => {
    if (show && pendingNotification) {
      if (pendingNotification.type === "success") {
        success(pendingNotification.title, pendingNotification.description);
      } else if (pendingNotification.type === "error") {
        notifyError(pendingNotification.title, pendingNotification.description);
      } else {
        info(pendingNotification.title, pendingNotification.description);
      }
      setPendingNotification(null);
    }
  };

  const toggleRow = (data: QuarantineDataType, skipChildReset: boolean = false) => {
    setSelectedRows((prevSelectedRows) => {
      const isRowSelected = prevSelectedRows.some((row) => row.id === data.id);
      if (isRowSelected) {
        return prevSelectedRows.filter((row) => row.id !== data.id);
      } else {
        // When selecting a parent row, clear all child exclusions so all children appear selected
        if (!skipChildReset && (data as any).urlGroups) {
          const childIds: string[] = [];
          if (selected === "Universities") {
            (data as any).urlGroups.forEach((g: any) => {
              (g.universities ?? []).forEach((u: any) => childIds.push(u.id));
            });
          } else if (selected === "Companies") {
            (data as any).urlGroups.forEach((g: any) => {
              (g.companyLocations ?? []).forEach((cl: any) => childIds.push(cl.id));
            });
          }
          if (childIds.length > 0) {
            setSelectedCompanyLocations((prev) =>
              prev.filter((id) => !childIds.includes(id))
            );
          }
        }
        return [...prevSelectedRows, data];
      }
    });
  };

  function handleSearch(event: React.ChangeEvent<HTMLInputElement>) {
    setPage(1);
    setQuery(event.currentTarget.value.toLowerCase());
  }

  async function handleCountSubmit() {
    if (!minCount || !maxCount || parseInt(maxCount) < parseInt(minCount)) {
      notifyError("Validation Error", "Please enter a valid Range.");
      return;
    }
    setQuarantineData([]);
    if (page === 1) {
      await fetchQuarantineData(undefined, undefined, buildFilters());
    } else {
      setPage(1);
    }
  }

  const handleCountKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === "Backspace" ||
      event.key === "Delete" ||
      event.key === "Tab" ||
      event.key === "Escape" ||
      event.key === "Enter"
    ) {
      return;
    }

    if (
      (event.ctrlKey || event.metaKey) &&
      (event.key === "a" ||
        event.key === "c" ||
        event.key === "v" ||
        event.key === "x")
    ) {
      return;
    }

    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  const handleLocationFilterChange = async (changedOption: FilterOption) => {
    const updatedOptions = locationFilter.options.map((option) => {
      if (option.value === changedOption.value) {
        return { ...option, checked: !option.checked };
      }
      return option;
    });
    const updatedFilteredOptions = locationFilter.filteredOptions.map((fo) => {
      if (fo.value === changedOption.value) {
        return { ...fo, checked: !fo.checked };
      }
      return fo;
    });
    setLocationFilter((prev) => ({
      ...prev,
      options: updatedOptions,
      filteredOptions: updatedFilteredOptions,
    }));
    setQuarantineData([]);

    // Build filters using updatedOptions directly to avoid stale state
    const filters: Filters[] = [];
    if (currentConfig.showCountFilter) {
      filters.push({
        FilterType: "count",
        MinCount: parseInt(minCount),
        MaxCount: parseInt(maxCount),
      });
    }
    if (currentConfig.showLocationFilter) {
      filters.push({
        FilterType: "location",
        Locations: updatedOptions
          .filter((o) => o.checked)
          .map((o) => o.label),
      });
    }

    if (page === 1) {
      await fetchQuarantineData(undefined, undefined, filters);
    } else {
      setPage(1);
    }
  };

  const handleTagFilter = async (tagsFilter: any) => {
    await fetchQuarantineData(undefined, undefined, tagsFilter);
  };

  const handleStateFilterChange = (stateId: string) => {
    setSelectedStates((prevSelected) =>
      prevSelected.includes(stateId)
        ? prevSelected.filter((id) => id !== stateId)
        : [...prevSelected, stateId]
    );
  };

  const handleFindGroups = async () => {
    setIsLoadingModal(true);
    setModalError(null);
    setIsSimilarCityModalOpen(true);

    try {
      const response = await getSimilarCityGroupsWithSuggestions(0.88, 1, 500, token || "");
      setSimilarCityGroups(response.groups);
      setTotalSimilarGroups(response.totalGroups);
    } catch (err: any) {
      console.error("❌ Error fetching city groups:", err.message);
      setModalError(err.message);
    } finally {
      setIsLoadingModal(false);
    }
  };

  const fetchSimilarUniversities = async (page: number = 1) => {
    setIsLoadingUniModal(true);
    setUniModalError(null);
    setUniModalPage(page);
    try {
      const response = await getSimilarUniversityGroups(0.90, page, 10, token || "");
      setSimilarUniGroups(response.groups);
      setTotalSimilarUniGroups(response.totalGroups);
    } catch (err: any) {
      setUniModalError(err.message);
    } finally {
      setIsLoadingUniModal(false);
    }
  };

  const handleFindSimilarUniversities = async () => {
    setIsSimilarUniModalOpen(true);
    await fetchSimilarUniversities(1);
  };

  const handleRecomputeUniGroups = async () => {
    setIsLoadingUniModal(true);
    try {
      await refreshSimilarUniversityGroupsCache(token || "");
      await fetchSimilarUniversities(1);
    } catch (err: any) {
      setUniModalError(err.message);
      setIsLoadingUniModal(false);
    }
  };

  const fetchSimilarCompanies = async (page: number = 1) => {
    setIsLoadingCompanyModal(true);
    setCompanyModalError(null);
    setCompanyModalPage(page);
    try {
      const response = await getSimilarCompanyGroups(0.90, page, 10, token || "");
      setSimilarCompanyGroups(response.groups);
      setTotalSimilarCompanyGroups(response.totalGroups);
    } catch (err: any) {
      setCompanyModalError(err.message);
    } finally {
      setIsLoadingCompanyModal(false);
    }
  };

  const handleFindSimilarCompanies = async () => {
    setIsSimilarCompanyModalOpen(true);
    await fetchSimilarCompanies(1);
  };

  const handleRecomputeCompanyGroups = async () => {
    setIsLoadingCompanyModal(true);
    try {
      await refreshSimilarCompanyGroupsCache(token || "");
      await fetchSimilarCompanies(1);
    } catch (err: any) {
      setCompanyModalError(err.message);
      setIsLoadingCompanyModal(false);
    }
  };

  const refreshTags = () => {
    if (!token) return;
    let tags: any[] = [];
    
    getTags(NIL, currentConfig.isCompanyType, "quarantine", token).then((t) => {
      if (t && t.length > 0) {
        tags = t.map((crt: any) => ({
          id: crt.id,
          name: crt.name,
          count: crt.count,
          subtags: crt.subtags
        }));
      }
      setTags(tags);
    });
  };

  const fetchQuarantineData = async (
    sortType = "Name",
    sortDesc?: boolean,
    filters?: Filters[]
  ) => {
    if (page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    debounceTimeout.current = setTimeout(async () => {
      if (!token) return;
      
      try {
        const { records, totalRecords } = await currentConfig.fetchData(
          token,
          page,
          40,
          query,
          sortType,
          sortDesc,
          filters,
          univType,
          cityType,
          selectedStates,
          showConfirmed
        );
        
        if (records) {
          setTotalRecords(totalRecords);
          if (query !== "" && page === 1) setQuarantineData(records);
          else if (page === 1) setQuarantineData(records);
          else setQuarantineData((prev) => [...prev, ...records]);

          if (totalRecords) setTotalPages(Math.ceil(totalRecords / 40));
        }
      } catch (err) {
        console.error("Error fetching quarantine data:", err);
      }
      
      setLoading(false);
      setLoadingMore(false);
    }, 500);
  };

  const getgptcitydetails = (cityId: string, details: any) => {
    if (!cityId) {
      // Clear all details if no cityId provided
      setGptCityDetails({});
    } else {
      // Merge details for the specific city
      setGptCityDetails((prev) => ({
        ...prev,
        [cityId]: details,
      }));
    }
  };

  const filteredOptions: City[] = cities.filter((option) => {
    const lowerSearchValue = query.toLowerCase();
    const nameMatches = option.name.toLowerCase().includes(lowerSearchValue);
    const alternateNames = option.alternateNames ? option.alternateNames.split(' ') : [];
    const alternateMatches = alternateNames.some((altName) =>
      altName.toLowerCase().includes(lowerSearchValue)
    );
    return nameMatches || alternateMatches;
  });

  const handleOptionSelect = (option: City) => {
    setSelectedOption(option);
    setQuery(option.name);
  };

  const handleSearchClear = () => {
    setSelectedOption(null);
    setQuery('');
    setPage(1);
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="bg-transparent">
      <div className="relative flex">
        {/* SIDEBAR */}
        <aside
          className={`dark:border-gray-500 fixed z-10 block h-[calc(100vh-4rem)] left-0 shadow-md border border-gray-200 transition-transform transform duration-200 w-[19rem] ${
            isSidebarOpen ? "translate-x-0" : "-translate-x-[100%]"
          }`}
        >
          {/* Tab Navigation */}
          <TabNavigation tabs={TABS} selected={selected} />

          {/* Search Input */}
          <div className="flex flex-row justify-end items-end sm:my-4 w-full">
            <SearchInput
              searchValue={query}
              selectedOption={selectedOption}
              filteredOptions={filteredOptions}
              disabled={showActions}
              onSearchChange={handleSearch}
              onOptionSelect={handleOptionSelect}
              onClear={handleSearchClear}
            />
          </div>

          {/* Actions Log Toggle */}
          <div
            className="flex w-full items-center justify-between py-2 px-3 text-gray-400 cursor-pointer hover:text-gray-900"
            onClick={() => setShowActions((prev) => !prev)}
          >
            <span className="font-medium text-sm text-gray-900 dark:text-gray-300">
              Actions Log
            </span>
            {showActions ? (
              <XMarkIcon className="w-5 text-gray-900" aria-hidden="true" />
            ) : (
              <History className="w-4" aria-hidden="true" />
            )}
          </div>

          {/* DYNAMIC FILTERS BASED ON TAB CONFIG */}
          
          {/* Count Filter */}
          {currentConfig.showCountFilter && (
            <CountFilter
              minCount={minCount}
              maxCount={maxCount}
              disabled={showActions}
              onMinChange={setMinCount}
              onMaxChange={setMaxCount}
              onApply={handleCountSubmit}
              onKeyDown={handleCountKeyDown}
            />
          )}

          {/* Type Filter */}
          {currentConfig.showTypeFilter && (
            <TypeFilter
              title={`${selected === "Universities" ? "University" : "Company"} Type`}
              selectedType={univType}
              options={TYPE_FILTER_OPTIONS}
              onChange={setUnivType}
            />
          )}

          {/* City Type Filter */}
          {currentConfig.showCityTypeFilter && (
            <TypeFilter
              title="City"
              selectedType={cityType}
              options={TYPE_FILTER_OPTIONS}
              onChange={setCityType}
            />
          )}

          {/* Location Filter */}
          {currentConfig.showLocationFilter && (
            <LocationFilterComponent
              locationFilter={locationFilter}
              locationSearch={locationSearch}
              disabled={showActions}
              onSearchChange={setLocationSearch}
              onOptionChange={handleLocationFilterChange}
            />
          )}

          {/* State Filter */}
          {currentConfig.showStateFilter && (
            <StateFilterComponent
              states={filterState}
              stateSearch={stateSearch}
              selectedStates={selectedStates}
              disabled={showActions}
              showConfirmed={showConfirmed}
              onSearchChange={setStateSearch}
              onStateChange={handleStateFilterChange}
              onConfirmedChange={(checked) => {
                setScrollToTop(true);
                setQuarantineData([]);
                setLoading(true);
                setPage(0);
                setShowConfirmed(checked);
              }}
            />
          )}

          {/* Sidebar Toggle */}
          <SidebarToggle
            isOpen={isSidebarOpen}
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          />

          {/* Tag Filter */}
          <TagFilter
            refreshTags={refreshTags}
            tags={tags}
            tab={selected}
            applyFilters={(filter: any) => handleTagFilter(filter)}
            view="quarantine"
          />

          {/* Similar City Groups Button */}
          {currentConfig.showSimilarCityGroups && (
            <div className="border-b border-gray-200 py-4 px-3 dark:border-gray-500">
              <div className="text-center">
                <button
                  onClick={handleFindGroups}
                  disabled={isLoadingModal}
                  className="w-full cursor-pointer rounded-md border-none bg-entntblue px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-entntorange disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {isLoadingModal ? 'Searching...' : 'Find Similar City Groups'}
                </button>
              </div>
            </div>
          )}

          {/* Similar University Groups Button */}
          {selected === TABS[1] && (
            <div className="border-b border-gray-200 py-4 px-3 dark:border-gray-500">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleFindSimilarUniversities}
                  disabled={isLoadingUniModal}
                  className="flex-1 cursor-pointer rounded-md border-none bg-entntblue px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-entntorange disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {isLoadingUniModal ? 'Searching...' : 'Find Similar University Groups'}
                </button>
                <InformationCircleIcon
                  data-tooltip-id="uni-groups-info"
                  className="w-6 h-6 text-gray-800 dark:text-gray-300 cursor-pointer shrink-0"
                />
              </div>
            </div>
          )}

          {/* Similar Company Groups Button */}
          {selected === TABS[0] && (
            <div className="border-b border-gray-200 py-4 px-3 dark:border-gray-500">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleFindSimilarCompanies}
                  className="flex-1 cursor-pointer rounded-md border-none bg-entntblue px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-entntorange disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  Find Similar Company Groups
                </button>
                <InformationCircleIcon
                  data-tooltip-id="company-groups-info"
                  className="w-6 h-6 text-gray-800 dark:text-gray-300 cursor-pointer shrink-0"
                />
              </div>
            </div>
          )}

          {/* Tag Management Buttons */}
          {currentConfig.showTagManagement && (
            <div className="w-full mt-10 flex gap-2">
              <button 
                className="inline-flex gap-2 items-center rounded-md bg-entntblue px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-entntorange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:hover:bg-entntblue disabled:cursor-not-allowed ml-5"
                onClick={() => setIsManagementModal(true)}
              >
                Tag management
              </button>
              {selectedRows.length > 0 && (
                <button 
                  className="inline-flex gap-2 items-center rounded-md bg-entntblue px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-entntorange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:hover:bg-entntblue disabled:cursor-not-allowed ml-5"
                  onClick={() => setAssignTagModal(true)}
                >
                  Tag action
                </button>
              )}
            </div>
          )}
        </aside>

        {/* MAIN CONTENT */}
        <main
          className={`${
            !isSidebarOpen
              ? "w-full"
              : "relative sm:transform translate-x-[19rem] sm:w-[calc(100%-19rem)]"
          } transition-all duration-200 ease-out`}
        >
          {loading === false && (!quarantineData || quarantineData.length === 0) ? (
            <div className="pt-8">
              <div className="bg-blue-100 rounded-lg py-8 dark:bg-darkbglight font-bold items-center text-center dark:text-gray-400">
                <h1>No new {selected}</h1>
              </div>
            </div>
          ) : (
            <div className="relative w-full">
              {showActions ? (
                <Actions
                  selected={selected}
                  loading={loading}
                  setLoading={setLoading}
                  setShowActions={setShowActions}
                />
              ) : (
                <>
                  <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                    <TableHeader
                      quarantineData={quarantineData}
                      setQuarantineData={setQuarantineData}
                      selected={selected}
                      toggleRow={toggleRow}
                      sortOption={sortOption}
                      setSortOption={setSortOption}
                      selectedRows={selectedRows}
                      setSelectedRows={setSelectedRows}
                      fetchQuarantineData={fetchQuarantineData}
                      token={token ?? ""}
                      selectedCompanyLocations={selectedCompanyLocations}
                      setSelectedCompanyLocations={setSelectedCompanyLocations}
                      setPage={setPage}
                      states={states}
                      showConfirmed={showConfirmed}
                      setShowRejectModalParent={setShowRejectModalParent}
                      setSubCityClickPopUpParent={setSubCityClickPopUpParent}
                      setMajorCityClickPopUpParent={setMajorCityClickPopUpParent}
                      showRejectModalParent={showRejectModalParent}
                      subCityClickPopUpParent={subCityClickPopUpParent}
                      majorCityClickPopUpParent={majorCityClickPopUpParent}
                      showConfirmedCities={showConfirmed}
                      gptCityDetails={gptCityDetails}
                      getgptcitydetails={getgptcitydetails}
                      pageNumber={page}
                      setPageNumber={setPage}
                      alternateNameClickPopUpParent={alternateNameClickPopUpParent}
                      setAlternateNameClickPopUpParent={setAlternateNameClickPopUpParent}
                      urlValidationResults={urlValidationResults}
                    />

                    <DataGrid
                      loading={loading}
                      setLoading={setLoading}
                      fetchQuarantineData={fetchQuarantineData}
                      selected={selected}
                      quarantineData={quarantineData}
                      setQuarantineData={setQuarantineData}
                      token={token ?? ""}
                      totalPages={Math.ceil(totalRecords / 13)}
                      currentPage={page}
                      setCurrentPage={setPage}
                      sortOption={sortOption}
                      setSortOption={setSortOption}
                      selectedRows={selectedRows}
                      setSelectedRows={setSelectedRows}
                      toggleRow={toggleRow}
                      selectedCompanyLocations={selectedCompanyLocations}
                      setSelectedCompanyLocations={setSelectedCompanyLocations}
                      showConfirmed={showConfirmed}
                      setShowRejectModalParent={setShowRejectModalParent}
                      setSubCityClickPopUpParent={setSubCityClickPopUpParent}
                      setMajorCityClickPopUpParent={setMajorCityClickPopUpParent}
                      getgptcitydetails={getgptcitydetails}
                      pageNumber={page}
                      setPageNumber={setPage}
                      loadingMore={loadingMore}
                      urlValidationResults={urlValidationResults}
                      setUrlValidationResults={setUrlValidationResults}
                    />
                  </table>
                </>
              )}
            </div>
          )}
        </main>

        {/* Scroll to Top Button */}
        {scrollToTop && (
          <button
            className="bg-entntblue h-6 w-6 rounded-full sticky left-[95%] top-[90vh] text-white transform transition-all hover:-translate-y-1"
            onClick={() => {
              window.scrollTo({
                top: 0,
                behavior: "smooth",
              });
            }}
          >
            <ChevronDoubleUpIcon />
          </button>
        )}
      </div>

      {/* MODALS */}
      {currentConfig.showTagManagement && (
        <>
          <TagManagementModal
            isOpen={isManagementModal}
            onCancel={() => setIsManagementModal(false)}
            title="Tag Management"
            tab={selected}
            setNotificationPopUp={(popUp: PopUp) => setPendingNotification(popUp)}
            refreshTags={() => {}}
            setShow={handleSetShow}
            getData={fetchQuarantineData}
            view="quarantine"
          />
          
          <AssignTagModal
            isOpen={assignTagModal}
            onCancel={() => setAssignTagModal(false)}
            title="Tag Management"
            tab={selected}
            setNotificationPopUp={(popUp: PopUp) => setPendingNotification(popUp)}
            refreshTags={() => {}}
            setShow={handleSetShow}
            selectedColumns={selectedRows.map((sr) => sr.id)}
            getData={() => fetchQuarantineData(undefined, undefined)}
            view="quarantine"
          />
        </>
      )}
      
      {currentConfig.showSimilarCityGroups && (
        <SimilarCityGroupsModal
          isOpen={isSimilarCityModalOpen}
          onClose={() => setIsSimilarCityModalOpen(false)}
          groups={similarCityGroups}
          totalGroups={totalSimilarGroups}
          isLoading={isLoadingModal}
          error={modalError}
          onRefresh={() => {
            handleFindGroups();
            setPage(1);
            setQuarantineData([]);
            fetchQuarantineData();
          }}
          token={token || ""}
        />
      )}
      <SimilarUniversityGroupsModal
        isOpen={isSimilarUniModalOpen}
        onClose={() => setIsSimilarUniModalOpen(false)}
        groups={similarUniGroups}
        totalGroups={totalSimilarUniGroups}
        isLoading={isLoadingUniModal}
        error={uniModalError}
        onRemoveGroup={(suggestedName: string) => {
          setSimilarUniGroups(prev => prev.filter(g => g.suggestedCorrectName !== suggestedName));
          setTotalSimilarUniGroups(prev => prev - 1);
          setPage(1);
          setQuarantineData([]);
          fetchQuarantineData();
        }}
        token={token || ""}
        currentPage={uniModalPage}
        onPageChange={(page: number) => {
          fetchSimilarUniversities(page);
          setPage(1);
          setQuarantineData([]);
          fetchQuarantineData();
        }}
        onRecompute={handleRecomputeUniGroups}
      />
      <SimilarCompanyGroupsModal
        isOpen={isSimilarCompanyModalOpen}
        onClose={() => setIsSimilarCompanyModalOpen(false)}
        groups={similarCompanyGroups}
        totalGroups={totalSimilarCompanyGroups}
        isLoading={isLoadingCompanyModal}
        error={companyModalError}
        onRemoveGroup={(suggestedName: string) => {
          setSimilarCompanyGroups(prev => prev.filter(g => g.suggestedCorrectName !== suggestedName));
          setTotalSimilarCompanyGroups(prev => prev - 1);
          setPage(1);
          setQuarantineData([]);
          fetchQuarantineData();
        }}
        token={token || ""}
        currentPage={companyModalPage}
        onPageChange={(page: number) => {
          fetchSimilarCompanies(page);
          setPage(1);
          setQuarantineData([]);
          fetchQuarantineData();
        }}
        onRecompute={handleRecomputeCompanyGroups}
      />
      <ReactTooltip
        key="uni-groups-info"
        id="uni-groups-info"
        render={() => (
          <div className="flex flex-col gap-2 text-sm">
            <p>Groups universities by similar names, matching URLs, and abbreviations. AI then validates whether grouped entries are truly the same institution.</p>
            <p className="font-semibold">Color coding:</p>
            <div className="flex flex-col gap-1">
              <span className="flex items-center gap-1.5 whitespace-nowrap"><span className="inline-block w-3 h-3 rounded bg-green-500 shrink-0" /> Suggested correct name</span>
              <span className="flex items-center gap-1.5 whitespace-nowrap"><span className="inline-block w-3 h-3 rounded bg-blue-500 shrink-0" /> AI confirmed — same university</span>
              <span className="flex items-center gap-1.5 whitespace-nowrap"><span className="inline-block w-3 h-3 rounded bg-red-500 shrink-0" /> AI flagged — likely different university</span>
            </div>
          </div>
        )}
      />
    </div>
  );
}

export default Quarantine;